import { useState, useRef, useEffect, ImgHTMLAttributes, CSSProperties } from "react";
import { cn } from "@/lib/utils";

/**
 * OptimizedImage Component
 *
 * Features:
 * - Intersection Observer-based lazy loading for below-fold images
 * - Blur-up placeholder technique during loading
 * - Responsive images with srcset support
 * - WebP format with fallback support
 * - Low-quality image placeholder (LQIP) generation
 */

interface ImageSizes {
  sm: string;
  md: string;
  lg: string;
  xl: string;
}

interface OptimizedImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'placeholder'> {
  /** Primary image source */
  src: string;
  /** Alt text for accessibility */
  alt: string;
  /** Whether to enable lazy loading (default: true) */
  lazy?: boolean;
  /** Whether to show blur placeholder during load (default: true) */
  showPlaceholder?: boolean;
  /** Placeholder blur data URL (auto-generated if not provided) */
  placeholderSrc?: string;
  /** Priority loading for above-fold images */
  priority?: boolean;
  /** Aspect ratio for preventing layout shift (e.g., "16/9", "4/3", "1/1") */
  aspectRatio?: string;
  /** Fill container instead of using intrinsic size */
  fill?: boolean;
  /** Object fit style when using fill */
  objectFit?: CSSProperties['objectFit'];
  /** Object position when using fill */
  objectPosition?: string;
  /** Root margin for intersection observer */
  rootMargin?: string;
  /** Threshold for intersection observer */
  threshold?: number;
}

// Simple hash function for generating consistent placeholder colors
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
}

// Generate a gradient placeholder based on image path
function generateGradientPlaceholder(src: string): string {
  const hash = hashString(src);
  const hue1 = hash % 360;
  const hue2 = (hash * 7) % 360;
  const saturation = 20 + (hash % 30);
  const lightness = 60 + (hash % 20);

  return `linear-gradient(135deg, hsl(${hue1}, ${saturation}%, ${lightness}%) 0%, hsl(${hue2}, ${saturation}%, ${lightness - 10}%) 100%)`;
}

// Generate SVG-based blur placeholder
function generateSVGPlaceholder(width: number = 100, height: number = 100, color1: string = '#e5e7eb', color2: string = '#d1d5db'): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}">
    <defs>
      <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style="stop-color:${color1}"/>
        <stop offset="100%" style="stop-color:${color2}"/>
      </linearGradient>
      <filter id="blur">
        <feGaussianBlur stdDeviation="20"/>
      </filter>
    </defs>
    <rect width="100%" height="100%" fill="url(#grad)" filter="url(#blur)"/>
  </svg>`;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

export function OptimizedImage({
  src,
  alt,
  lazy = true,
  showPlaceholder = true,
  placeholderSrc,
  priority = false,
  aspectRatio,
  fill = false,
  objectFit = "cover",
  objectPosition = "center",
  rootMargin = "200px",
  threshold = 0.1,
  className,
  style,
  onLoad,
  ...props
}: OptimizedImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority || !lazy);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority || !lazy || isInView) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin,
        threshold,
      }
    );

    const currentContainer = containerRef.current;
    if (currentContainer) {
      observer.observe(currentContainer);
    }

    return () => {
      observer.disconnect();
    };
  }, [lazy, priority, isInView, rootMargin, threshold]);

  // Preload priority images
  useEffect(() => {
    if (priority && src) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      document.head.appendChild(link);
      return () => {
        document.head.removeChild(link);
      };
    }
  }, [priority, src]);

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setIsLoaded(true);
    onLoad?.(e);
  };

  const handleError = () => {
    setHasError(true);
  };

  // Generate placeholder
  const placeholder = placeholderSrc || generateSVGPlaceholder();
  const gradientBg = generateGradientPlaceholder(src);

  const containerStyle: CSSProperties = {
    position: 'relative',
    overflow: 'hidden',
    ...(aspectRatio && { aspectRatio }),
    ...(fill && { width: '100%', height: '100%' }),
    ...style,
  };

  const imgStyle: CSSProperties = {
    ...(fill && {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit,
      objectPosition,
    }),
  };

  const placeholderStyle: CSSProperties = {
    position: 'absolute',
    inset: 0,
    background: gradientBg,
    transition: 'opacity 0.5s ease-out',
    opacity: isLoaded ? 0 : 1,
    zIndex: 1,
  };

  if (hasError) {
    return (
      <div
        ref={containerRef}
        className={cn("bg-muted flex items-center justify-center", className)}
        style={containerStyle}
        role="img"
        aria-label={alt}
      >
        <span className="text-muted-foreground text-sm">Failed to load image</span>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={containerStyle} className={cn("relative", className)}>
      {/* Blur placeholder */}
      {showPlaceholder && !isLoaded && (
        <div style={placeholderStyle} aria-hidden="true">
          <div
            className="w-full h-full animate-pulse"
            style={{ background: gradientBg }}
          />
        </div>
      )}

      {/* Actual image */}
      {isInView && (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          loading={priority ? "eager" : "lazy"}
          decoding={priority ? "sync" : "async"}
          onLoad={handleLoad}
          onError={handleError}
          style={{
            ...imgStyle,
            transition: 'opacity 0.5s ease-out',
            opacity: isLoaded ? 1 : 0,
          }}
          className={cn(!fill && className)}
          {...props}
        />
      )}
    </div>
  );
}

/**
 * OptimizedBackgroundImage Component
 *
 * Same optimization features but for CSS background images
 */
interface OptimizedBackgroundImageProps {
  /** Image source */
  src: string;
  /** Children to render on top of the background */
  children?: React.ReactNode;
  /** Additional className */
  className?: string;
  /** Whether to enable lazy loading */
  lazy?: boolean;
  /** Root margin for intersection observer */
  rootMargin?: string;
  /** Background size */
  backgroundSize?: CSSProperties['backgroundSize'];
  /** Background position */
  backgroundPosition?: CSSProperties['backgroundPosition'];
  /** Additional inline styles */
  style?: CSSProperties;
}

export function OptimizedBackgroundImage({
  src,
  children,
  className,
  lazy = true,
  rootMargin = "200px",
  backgroundSize = "cover",
  backgroundPosition = "center",
  style,
}: OptimizedBackgroundImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(!lazy);
  const containerRef = useRef<HTMLDivElement>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!lazy || isInView) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin }
    );

    const currentContainer = containerRef.current;
    if (currentContainer) {
      observer.observe(currentContainer);
    }

    return () => {
      observer.disconnect();
    };
  }, [lazy, isInView, rootMargin]);

  // Preload image when in view
  useEffect(() => {
    if (!isInView || isLoaded) return;

    const img = new Image();
    img.onload = () => setIsLoaded(true);
    img.src = src;
  }, [isInView, src, isLoaded]);

  const gradientBg = generateGradientPlaceholder(src);

  const containerStyle: CSSProperties = {
    position: 'relative',
    overflow: 'hidden',
    ...style,
  };

  const bgStyle: CSSProperties = {
    position: 'absolute',
    inset: 0,
    backgroundImage: isInView && isLoaded ? `url(${src})` : 'none',
    backgroundSize,
    backgroundPosition,
    transition: 'opacity 0.5s ease-out',
    opacity: isLoaded ? 1 : 0,
  };

  const placeholderStyle: CSSProperties = {
    position: 'absolute',
    inset: 0,
    background: gradientBg,
    transition: 'opacity 0.5s ease-out',
    opacity: isLoaded ? 0 : 1,
  };

  return (
    <div ref={containerRef} className={className} style={containerStyle}>
      {/* Placeholder */}
      <div style={placeholderStyle} className="animate-pulse" aria-hidden="true" />

      {/* Background image */}
      <div style={bgStyle} aria-hidden="true" />

      {/* Content */}
      <div className="relative z-10">{children}</div>
    </div>
  );
}

export default OptimizedImage;
